import type {
  ApiErrorBody,
  AppConfig,
  Category,
  ChatMessage,
  ChatRoom,
  Collection,
  CreateCategoryInput,
  CreatePostInput,
  CreateThreadInput,
  CurrentUser,
  HidePostInput,
  Post,
  ReactionName,
  Single,
  Thread,
  UpdateCategoryInput,
  UpdateMeInput,
  UserProfile,
} from "@talkers/shared";

/**
 * dev は **常に** Vite の proxy 経由 (same-origin の `/api`) にする。
 *
 * ルートの `.env` に `VITE_API_ORIGIN` があると bun 経由で dev ビルドにも入ってしまい、
 * ブラウザが API を直接叩いて CORS で落ちる (127.0.0.1 と localhost は別 origin)。
 * この変数は本番ビルド (Cloudflare Pages → Cloud Run) 用と割り切る。
 */
const API_ORIGIN = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_ORIGIN ?? "");
const BASE = `${API_ORIGIN}/api/v1`;

/** API が返した構造化エラー。UI は `code` で分岐する。 */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof value.error === "object" &&
    value.error !== null &&
    "code" in value.error
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body === undefined ? {} : { "content-type": "application/json" }),
      ...init?.headers,
    },
    // Phase4 のセッション Cookie に備えて常に送る
    credentials: "include",
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    if (isErrorBody(body)) {
      throw new ApiRequestError(
        response.status,
        body.error.code,
        body.error.message,
        body.error.requestId,
      );
    }
    throw new ApiRequestError(response.status, "UNKNOWN", `HTTP ${response.status}`, "");
  }

  return (await response.json()) as T;
}

const search = (params: Record<string, string | number | boolean | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const text = query.toString();
  return text === "" ? "" : `?${text}`;
};

export const api = {
  getConfig: () => request<Single<AppConfig>>("/config"),

  /** セッションが無ければ data は null。 */
  getMe: () => request<Single<CurrentUser | null>>("/me"),

  /**
   * 名乗っただけのゲストとしてセッションを始める。
   * better-auth のエンドポイントなので `/api/v1` の外にある。
   */
  signInAnonymous: async (): Promise<void> => {
    const response = await fetch(`${API_ORIGIN}/api/auth/sign-in/anonymous`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
      credentials: "include",
    });
    if (!response.ok) throw new Error("ゲストとして始められませんでした");
  },

  updateMe: (input: UpdateMeInput) =>
    request<Single<CurrentUser>>("/me", { method: "PATCH", body: JSON.stringify(input) }),

  /** CLI に出ているコードを、ログイン中のアカウントとして承認する。 */
  approveDevice: (userCode: string) =>
    request<Single<{ status: "approved" }>>("/device/approve", {
      method: "POST",
      body: JSON.stringify({ userCode }),
    }),

  denyDevice: (userCode: string) =>
    request<Single<{ status: "denied" }>>("/device/deny", {
      method: "POST",
      body: JSON.stringify({ userCode }),
    }),

  /** 管理者のみ。行は残したまま本文を伏せる / 戻す。 */
  /** 管理者のみ。スレッドごと伏せる / 戻す。 */
  setThreadHidden: (threadId: number, hidden: boolean) =>
    request<Single<Thread>>(`/threads/${threadId}/hidden`, {
      method: "PATCH",
      body: JSON.stringify({ hidden }),
    }),

  setPostHidden: (threadId: number, seq: number, input: HidePostInput) =>
    request<Single<Post>>(`/threads/${threadId}/posts/${seq}/hidden`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  setReaction: (threadId: number, seq: number, name: ReactionName, on: boolean) =>
    request<Single<Post>>(`/threads/${threadId}/posts/${seq}/reactions/${name}`, {
      method: on ? "PUT" : "DELETE",
    }),

  getProfile: (handle: string) =>
    request<Single<UserProfile>>(`/users/${encodeURIComponent(handle)}`),

  listCategories: (params: { includeHidden?: boolean } = {}) =>
    request<Single<Category[]>>(`/categories${search(params)}`),

  createCategory: (input: CreateCategoryInput) =>
    request<Single<Category>>("/categories", { method: "POST", body: JSON.stringify(input) }),

  updateCategory: (id: number, input: UpdateCategoryInput) =>
    request<Single<Category>>(`/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),

  listChatRooms: () => request<Single<ChatRoom[]>>("/chat/rooms"),

  openChatRoom: (userId: number) =>
    request<Single<ChatRoom>>("/chat/rooms", { method: "POST", body: JSON.stringify({ userId }) }),

  listChatMessages: (roomId: number) =>
    request<Single<ChatMessage[]>>(`/chat/rooms/${roomId}/messages`),

  sendChatMessage: (roomId: number, body: string) =>
    request<Single<ChatMessage>>(`/chat/rooms/${roomId}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  listThreads: (params: {
    category?: string;
    cursor?: string;
    limit?: number;
    includeHidden?: boolean;
  }) => request<Collection<Thread>>(`/threads${search(params)}`),

  getThread: (threadId: number) => request<Single<Thread>>(`/threads/${threadId}`),

  /** `rootLimit` を渡すと、新しい順に親ポスト N 件とその返信すべてが返る。 */
  listPosts: (
    threadId: number,
    params: { cursor?: string; limit?: number; rootLimit?: number } = {},
  ) => request<Collection<Post>>(`/threads/${threadId}/posts${search(params)}`),

  createThread: (input: CreateThreadInput) =>
    request<Single<Thread>>("/threads", { method: "POST", body: JSON.stringify(input) }),

  createPost: (threadId: number, input: CreatePostInput) =>
    request<Single<Post>>(`/threads/${threadId}/posts`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
};
